/** API client for engineering analysis endpoints. */

import { get, post } from './client.ts'
import type {
  TransformerLoadingSummary,
  FeederLoadingSummary,
  PhaseBalanceResult,
  VoltageDropResult,
  VoltageDropSummaryItem,
  WhatIfNewLoadRequest,
  WhatIfNewLoadResult,
  LoadSource,
} from '../types/engineering.ts'
import type { GeoJSONFeatureCollection } from '../types/topology.ts'

function buildQS(params: Record<string, string | number | undefined | null>): string {
  const qs = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null) qs.set(k, String(v))
  }
  const s = qs.toString()
  return s ? `?${s}` : ''
}

// Capacity
export function getTransformerLoading(params?: {
  feeder_id?: number
  substation_id?: number
  avg_demand_kw?: number
  load_source?: LoadSource
  window_days?: number
}): Promise<TransformerLoadingSummary> {
  return get<TransformerLoadingSummary>(
    `/engineering/capacity/transformers${buildQS(params ?? {})}`
  )
}

export function getFeederLoadingSummary(params?: {
  substation_id?: number
  avg_demand_kw?: number
  load_source?: LoadSource
  window_days?: number
}): Promise<FeederLoadingSummary[]> {
  return get<FeederLoadingSummary[]>(
    `/engineering/capacity/feeders${buildQS(params ?? {})}`
  )
}

export function getPhaseBalance(
  feederId: number,
  avgDemandKw?: number,
): Promise<PhaseBalanceResult> {
  return get<PhaseBalanceResult>(
    `/engineering/capacity/feeders/${feederId}/phase-balance${buildQS({ avg_demand_kw: avgDemandKw })}`
  )
}

// Voltage Drop
export function getVoltageDropForFeeder(
  feederId: number,
  params?: { avg_demand_kw?: number; power_factor?: number },
): Promise<VoltageDropResult> {
  return get<VoltageDropResult>(
    `/engineering/voltage-drop/feeders/${feederId}${buildQS(params ?? {})}`
  )
}

export function getVoltageDropSummary(params?: {
  substation_id?: number
  avg_demand_kw?: number
}): Promise<VoltageDropSummaryItem[]> {
  return get<VoltageDropSummaryItem[]>(
    `/engineering/voltage-drop/summary${buildQS(params ?? {})}`
  )
}

// What-If / New-Load Analysis
export function runWhatIfNewLoad(
  feederId: number,
  body: WhatIfNewLoadRequest,
): Promise<WhatIfNewLoadResult> {
  return post<WhatIfNewLoadResult>(`/engineering/what-if/feeders/${feederId}`, body)
}

// Map GeoJSON
export function getTransformerLoadingGeoJSON(params?: {
  feeder_id?: number
  avg_demand_kw?: number
  load_source?: LoadSource
  window_days?: number
}): Promise<GeoJSONFeatureCollection> {
  return get<GeoJSONFeatureCollection>(
    `/map/transformers/loading${buildQS(params ?? {})}`
  )
}

export function getConductorVoltageDropGeoJSON(params: {
  feeder_id: number
  avg_demand_kw?: number
  power_factor?: number
}): Promise<GeoJSONFeatureCollection> {
  return get<GeoJSONFeatureCollection>(
    `/map/conductors/voltage-drop${buildQS(params)}`
  )
}
